/**
 * Integrytas Group - Licensing -> menu gating.
 *
 * Decides which licensable (add-on) modules may appear in the navigation menu.
 * Source priority:
 *   1. Verified central licence (lic_status.modules) when licensing is ENFORCED
 *      (the cryptographically-signed entitlement - production multi-tenant).
 *   2. Local allow-list system parameter INTEGRYTAS.LICENSE.ENABLED_MODULES
 *      (is_public='Y') - self-host owner's choice when no central authority.
 *   3. Otherwise ungated - everything shows (no breakage on a fresh box).
 *
 * Core modules (csm and anything not in the licensable catalog) always show.
 */

import { apiDb } from './api_client';
import type { PageAccess } from '../types/auth';

// Mirror of LIC_STATIC_CATALOG in supabase/functions/csm-api/index.ts.
const STATIC_CATALOG = ['gsi', 'fdm', 'cbm', 'ike', 'dse', 'udc', 'phe', 'uld', 'dad', 'ror', 'cms', 'eds', 'cds', 'rpd'];

export interface menu_entitlement {
  active: boolean;          // is module gating in effect?
  catalog: Set<string>;     // licensable module codes
  entitled: Set<string>;    // module codes allowed to appear
}

export function module_code_of(internal_code: string): string {
  return internal_code.split('.')[0].toLowerCase();
}

export async function load_menu_entitlement(): Promise<menu_entitlement> {
  let catalog = new Set(STATIC_CATALOG);

  // 1. Central crypto licence (only governs when actually enforced).
  try {
    const { data } = await apiDb.licStatus<{ licensing_enabled: boolean; modules: string[]; catalog: string[] }>();
    if (data?.catalog?.length) catalog = new Set(data.catalog.map(c => c.toLowerCase()));
    if (data?.licensing_enabled) {
      return { active: true, catalog, entitled: new Set((data.modules ?? []).map(m => m.toLowerCase())) };
    }
  } catch { /* lic_status unavailable -> fall through */ }

  // 2. Self-host allow-list (public system parameter).
  try {
    const { data } = await apiDb
      .from('csm_system_parameters')
      .select('parameter_value')
      .eq('internal_code', 'INTEGRYTAS.LICENSE.ENABLED_MODULES');
    const raw = (data as { parameter_value: string }[] | null)?.[0]?.parameter_value;
    if (raw) {
      const arr = (JSON.parse(raw) as unknown[]).map(m => String(m).toLowerCase());
      return { active: true, catalog, entitled: new Set(arr) };
    }
  } catch { /* absent / unreadable -> ungated */ }

  // 3. Nothing configured -> show everything.
  return { active: false, catalog, entitled: catalog };
}

// Drop pages for un-entitled catalog modules, then prune now-empty group nodes
// (iteratively, so a group whose only children were gated also disappears).
export function filter_menu_by_entitlement(pages: PageAccess[], ent: menu_entitlement): PageAccess[] {
  if (!ent.active) return pages;

  const blocked = (code: string) => {
    const m = module_code_of(code);
    return ent.catalog.has(m) && !ent.entitled.has(m);
  };
  let kept = pages.filter(p => !blocked(p.page_internal_code));

  const groupIds = new Set<number>();
  for (const p of pages) if (p.parent_page_id != null) groupIds.add(p.parent_page_id);

  let changed = true;
  while (changed) {
    changed = false;
    const hasChild = new Set<number>();
    for (const p of kept) if (p.parent_page_id != null) hasChild.add(p.parent_page_id);
    const next = kept.filter(p => {
      if (groupIds.has(p.page_id) && !hasChild.has(p.page_id)) { changed = true; return false; }
      return true;
    });
    kept = next;
  }
  return kept;
}
